import {Card} from "primereact/card";

import type {GeoRefAddress} from "@reva/shared/geo-reference";

import {useRevaTranslation} from "../../hooks/languageHooks";

/** Ein Flurstück, wie es der Georef-Adressblock liefert (#19384). */
export interface CadastralParcel {
    municipality?: string;
    cadastral_district?: string;
    cadastral_district_key?: string;
    cadastral_key?: string;
    cadastral_parcel_number: string;
}

/**
 * Flurstücke einer Adresse. Heute höchstens das Flurstück am Adresspunkt; mehrere Flurstücke je Objekt sind in
 * #19744 offen und kämen hier als weitere Einträge hinzu.
 */
export function cadastralParcelsOfAddress(address: GeoRefAddress | null | undefined): CadastralParcel[] {
    if (!address?.cadastral_parcel_number) return [];
    return [
        {
            municipality: address.municipality,
            cadastral_district: address.cadastral_district,
            cadastral_district_key: address.cadastral_district_key,
            cadastral_key: address.cadastral_key,
            cadastral_parcel_number: address.cadastral_parcel_number,
        },
    ];
}

const PARCEL_FIELDS = [
    "municipality",
    "cadastral_district",
    "cadastral_district_key",
    "cadastral_key",
    "cadastral_parcel_number",
] as const satisfies readonly (keyof CadastralParcel)[];

interface CadastralParcelCardsProps {
    address: GeoRefAddress | null | undefined;
}

/** Je Flurstück eine Karte mit Gemeinde, Gemarkung, Gemarkungsschlüssel, Flurschlüssel und Flurstücksnummer. */
export default function CadastralParcelCards({address}: CadastralParcelCardsProps) {
    const {t} = useRevaTranslation();
    const parcels = cadastralParcelsOfAddress(address);

    return parcels.map((parcel) => (
        <Card
            key={`${parcel.cadastral_district_key ?? ""}-${parcel.cadastral_key ?? ""}-${parcel.cadastral_parcel_number}`}
            className="CadastralParcelCard LandRegisterCard MapNoteCard bg-surface-100 dark:bg-surface-700 mb-6 mt-0"
        >
            <h3 className="mt-0 mb-2">{t("landregister.parcel", {number: parcel.cadastral_parcel_number})}</h3>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 m-0">
                {PARCEL_FIELDS.map((field) => (
                    <div key={field} className="contents">
                        <dt>{t(`address.${field}`)}:</dt>
                        <dd className="m-0">{parcel[field] ?? "–"}</dd>
                    </div>
                ))}
            </dl>
        </Card>
    ));
}
